import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePageTitle } from '../hooks/usePageTitle';
import { useAuth } from '../context/AuthContext';
import { authAPI } from '../utils/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Loader2 } from 'lucide-react';
import Swal from 'sweetalert2';

const LOGO_URL = "https://drlp.atlantic4web.pt/logo-drleonardopacheco.jpg";
const APP_NAME = "Dr. Leonardo Pacheco";

export const Login = () => {
  const [email, setEmail] = useState('');
  usePageTitle('Iniciar Sessão');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [seeding, setSeeding] = useState(false);
  const { login, isAuthenticated } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/');
    }
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    // Seed admin on first load
    const seedAdmin = async () => {
      try {
        setSeeding(true);
        await authAPI.seedAdmin();
      } catch (error) {
        // Ignore if admin already exists
      } finally {
        setSeeding(false);
      }
    };
    seedAdmin();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email || !password) {
      Swal.fire({
        icon: 'warning',
        title: 'Campos obrigatórios',
        text: 'Por favor, preencha todos os campos.',
        confirmButtonColor: '#0D9488'
      });
      return;
    }

    setLoading(true);
    try {
      await login(email, password);
      Swal.fire({
        icon: 'success',
        title: 'Bem-vindo!',
        text: 'Login efetuado com sucesso.',
        timer: 1500,
        showConfirmButton: false
      });
      navigate('/');
    } catch (error) {
      console.error('Login error:', error);
      Swal.fire({
        icon: 'error',
        title: 'Erro no Login',
        text: error.response?.data?.detail || 'Credenciais inválidas. Tente novamente.',
        confirmButtonColor: '#0D9488'
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white flex items-center justify-center p-4" data-testid="login-page">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <img src={LOGO_URL} alt="Logo" style={{ width: '250px' }} className="mx-auto" />
        </div>

        <Card className="border-slate-200 shadow-sm">
          <CardHeader className="space-y-1 pb-4">
            <CardTitle className="text-xl font-semibold text-center">Iniciar Sessão</CardTitle>
            <CardDescription className="text-center">
              Introduza as suas credenciais para aceder
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-slate-700">Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="exemplo@email.pt"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="h-11"
                  data-testid="login-email-input"
                  disabled={loading || seeding}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password" className="text-slate-700">Password</Label>
                <Input
                  id="password"
                  type="password"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="h-11"
                  data-testid="login-password-input"
                  disabled={loading || seeding}
                />
              </div>
              <Button 
                type="submit" 
                className="w-full h-11 bg-amber-600 hover:bg-amber-700 text-white font-medium"
                disabled={loading || seeding}
                data-testid="login-submit-button"
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    A entrar...
                  </>
                ) : seeding ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    A preparar...
                  </>
                ) : (
                  'Entrar'
                )}
              </Button>
            </form>
            
          </CardContent>
        </Card>
        
        {/* Footer */}
        <div className="mt-6 text-center">
          <p className="text-xs text-slate-400">
            2026 © Dr. Leonardo Pacheco
          </p>
          <p className="text-xs text-slate-400">
            Desenvolvido por <a href="https://atlantic4web.pt" target="_blank" rel="noopener noreferrer" className="font-medium hover:text-amber-600">atlantic4web</a>
          </p>
        </div>
      </div>
    </div>
  );
};
